import { Box, Button, Checkbox, Input, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import React from "react";
import LabeledSelectNoId from "../../components/universal/LabeledSelectNoId";
import { NamedTagForSelectionFragment, PositionFragment, TeamMealMenuPreviewFragment } from "../../types";
import MenusTeamMultiSelect from "../Menus/SelectComponents/TeamMultiSelect";
import NamedTagSelector from "../Tags/Selectors/NamedTagSelector";

export const TEAM = "";

export type MenuAttendanceSortType = typeof TEAM;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    exportButton: {
      width: theme.spacing(25),
      marginTop: theme.spacing(2.5),
    },
    teamSelect: {
      width: theme.spacing(50),
    },
    menuItem: {
      backgroundColor: "white !important",
      "&:hover": {
        backgroundColor: `${theme.palette.grey[200]} !important`,
      },
    },
    positionSelect: {
      marginBottom: -30,
    },
    inputLabel: {
      fontWeight: theme.typography.fontWeightBold,
    },
  })
);

interface MealMenuAttendanceTableToolbarProps {
  selectedTeams: readonly TeamMealMenuPreviewFragment[];
  onChangeTeams: (selectedTeams: readonly TeamMealMenuPreviewFragment[]) => void;
  checkedIn: boolean | null;
  onChangeCheckedIn: (checkedIn: boolean | null) => void;
  selectNamedTags: NamedTagForSelectionFragment[];
  onChangeNamedTags: (newTags: NamedTagForSelectionFragment[]) => void;
  teams: { id: string; name: string }[];
  positions: PositionFragment[];
  selectedPositionNames: string[];
  handleSelectPosition: (event: SelectChangeEvent<string[]>) => void;
  exportData: () => void;
}

export const MealMenuAttendanceTableToolbar = ({
  teams,
  selectedTeams,
  onChangeTeams,
  checkedIn,
  onChangeCheckedIn,
  selectNamedTags,
  onChangeNamedTags,
  positions,
  selectedPositionNames,
  handleSelectPosition,
  exportData,
}: MealMenuAttendanceTableToolbarProps) => {
  const classes = useStyles();
  const selectedCheckedInOption = checkedIn === true ? "attended" : checkedIn === false ? "not-attended" : "all";
  const handleCheckinOnChange = (option: string) => {
    if (option === "attended") {
      onChangeCheckedIn(true);
    } else if (option === "not-attended") {
      onChangeCheckedIn(false);
    } else {
      onChangeCheckedIn(null);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", gap: 2, alignItems: "center", justifyContent: "flex-start" }}>
      <MenusTeamMultiSelect
        selectedTeams={selectedTeams}
        onChange={onChangeTeams}
        fullWidth={true}
        displayedTeamsCount={1}
        allTeams={[...teams.map(t => ({ ...t, athletes: [] }))]}
      />
      <Box sx={{ minWidth: "300px" }}>
        <NamedTagSelector
          selectedNamedTags={selectNamedTags}
          onChange={newNamedTags => onChangeNamedTags(newNamedTags)}
          limitTags={1}
          textInputLabel={"Tags"}
        />
      </Box>
      <Box sx={{ minWidth: "300px" }}>
        <InputLabel className={classes.inputLabel}>Positions</InputLabel>
        <Select
          multiple
          fullWidth={true}
          className={classes.positionSelect}
          value={selectedPositionNames}
          onChange={handleSelectPosition}
          input={<Input id="select-multiple-checkbox" />}
          renderValue={selected => (selected as string[]).join(", ")}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 48 * 7 + 8, // 7 items displayed by default, the rest can be scrolled to
                width: 250,
              },
            },
          }}
        >
          {positions
            .map(p => p.name)
            .map(posName => (
              <MenuItem
                dense
                key={posName}
                value={posName}
                classes={{ selected: classes.menuItem }}>
                <Checkbox checked={selectedPositionNames.includes(posName)} />
                <ListItemText primary={posName} />
              </MenuItem>
            ))}
        </Select>
      </Box>
      <LabeledSelectNoId<string>
        label="Checked In"
        placeholder=""
        optionToName={o => (o === "attended" ? "Attended" : o === "not-attended" ? `Not Attended` : "All")}
        selectedOption={selectedCheckedInOption}
        options={["all", "attended", "not-attended"]}
        onChange={handleCheckinOnChange}
      />
      <Button className={classes.exportButton} onClick={exportData}>
        Export
      </Button>
    </Box>
  );
};
